import { useCallback, useEffect, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { fetchCarrierAccessList, mapCarrierAccessApiItemToCarrierAccessItem } from "./requests/request";
import type { CarrierAccessItem } from "./carrierAccess.types";

/**
 * Carrier Access — list data hook. Aborts in-flight requests when the tenant changes
 * or the component unmounts, so stale responses never land in state.
 */
export function useCarrierAccessList() {
  const axiosAuth = useAxiosAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const [items, setItems] = useState<CarrierAccessItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId == null) return;
      setLoading(true);
      setError(null);
      try {
        const response = await fetchCarrierAccessList(axiosAuth, tenantId, signal);
        if (signal?.aborted) return;
        if (response.data?.success === false) {
          setItems([]);
          setError(response.data.message || "Failed to load Carrier Access.");
          return;
        }
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        setItems(rows.map(mapCarrierAccessApiItemToCarrierAccessItem));
      } catch (err) {
        if (signal?.aborted) return;
        setError("Failed to load Carrier Access. Please try again.");
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [axiosAuth, tenantId]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return { items, loading, error, refetch: () => load() };
}
